/**
 * gamepad-connection-store — the ONE place that decides a controller arrived or left.
 *
 * A module singleton, following the precedent already in this app (`robot-alerts-store`,
 * `sos-call-store`). Being the single emitter is what makes "announced exactly once" a structural
 * property rather than a rule two components have to keep agreeing on.
 *
 * NEVER STOPS ONCE STARTED. Tearing down on the last unsubscribe would re-arm the baseline on the
 * next mount and silently swallow any edge that happened in between — a worse failure than two
 * idle listeners for the life of the document.
 */

import { defaultGetGamepads, type GetGamepads } from './gamepad-dom'
import {
  CONNECTION_DEBOUNCE_MS,
  diffConnection,
  readGamepadState,
  type GamepadConnectionEvent,
  type GamepadConnectionState,
} from './gamepad-connection'

type Listener = (event: GamepadConnectionEvent) => void

const listeners = new Set<Listener>()

let started = false
/** The last state we have ANNOUNCED. Every diff is against this, never against the last raw read. */
let announced: GamepadConnectionState = { connected: false, id: null }
let timer: ReturnType<typeof setTimeout> | null = null
let owners = 0
let toastSuppressors = 0

let getGamepads: GetGamepads = defaultGetGamepads
let now: () => number = () => Date.now()

function settleNow(): void {
  timer = null
  const current = readGamepadState(getGamepads())
  const event = diffConnection(announced, current, now())

  // Advance the baseline UNCONDITIONALLY. A brown-out that nets to no change still has to move
  // the baseline forward, or the next genuine edge would be diffed against a stale state.
  announced = current

  if (!event) return

  for (const listener of [...listeners]) listener(event)
}

function schedule(): void {
  // COALESCING, not trailing-per-event: each raw event re-arms the SAME timer, so a
  // disconnect/connect pair inside the window resolves to one comparison against `announced`.
  if (timer !== null) clearTimeout(timer)
  timer = setTimeout(settleNow, CONNECTION_DEBOUNCE_MS)
}

function start(): void {
  if (started) return
  started = true

  // ARM THE BASELINE SILENTLY. This one line is "no announcement on page load": a pad already
  // attached when the console opens is steady state, not something that just happened.
  announced = readGamepadState(getGamepads())

  if (typeof window === 'undefined') return
  window.addEventListener('gamepadconnected', schedule)
  window.addEventListener('gamepaddisconnected', schedule)
}

export function subscribeGamepadConnection(listener: Listener): () => void {
  start()
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export interface GamepadNoticeOwnership {
  /** True only for the FIRST live acquirer — so two mounted surfaces do not both speak. */
  primary: boolean
  release: () => void
}

/**
 * Claim responsibility for announcing locally.
 *
 * TWO SEPARATE QUESTIONS, and conflating them made the robot silent on the surface an operator
 * actually uses. "Who speaks" is a primacy question — exactly one surface may, or two mounted
 * robot panes talk over each other. "Does the app toast stand down" is a VISIBILITY question, and
 * the only surface that answers yes is the teleop overlay, where the toast is provably invisible
 * (z-50 under AppLayout against the overlay's `fixed inset-0 z-[90]` inside the real Fullscreen
 * API). A robot tile on an ordinary dashboard speaks AND leaves the toast alone, because there
 * the toast is perfectly visible and the two say different things.
 */
export function acquireGamepadNoticeOwnership(
  opts: { suppressesToast?: boolean } = {},
): GamepadNoticeOwnership {
  const suppresses = opts.suppressesToast ?? true
  owners += 1
  if (suppresses) toastSuppressors += 1
  const primary = owners === 1
  let released = false

  return {
    primary,
    release: () => {
      // Guarded: React can invoke a cleanup more than once, and a count driven below zero would
      // make the NEXT acquirer non-primary — a silent robot instead of a speaking one.
      if (released) return
      released = true
      owners = Math.max(0, owners - 1)
      if (suppresses) toastSuppressors = Math.max(0, toastSuppressors - 1)
    },
  }
}

/** True only while a surface is held that the app toast would be INVISIBLE behind. */
export function hasGamepadNoticeOwner(): boolean {
  return toastSuppressors > 0
}

/** Test seam. Never called by application code. */
export function __setGamepadConnectionDeps(deps: { getGamepads?: GetGamepads; now?: () => number }): void {
  if (deps.getGamepads) getGamepads = deps.getGamepads
  if (deps.now) now = deps.now
}

/** Test seam: the only teardown that exists, because production never stops. */
export function __resetGamepadConnectionForTests(): void {
  if (timer !== null) clearTimeout(timer)
  timer = null
  listeners.clear()
  owners = 0
  toastSuppressors = 0
  announced = { connected: false, id: null }
  getGamepads = defaultGetGamepads
  now = () => Date.now()

  if (started && typeof window !== 'undefined') {
    window.removeEventListener('gamepadconnected', schedule)
    window.removeEventListener('gamepaddisconnected', schedule)
  }

  started = false
}
